/*
 * Live presence on a forum topic.
 *
 * Two things happen on one Reverb presence channel:
 *
 *   who is here   join and leave events tell the Livewire component to re-render itself,
 *                 rather than the member list being rebuilt in JavaScript. The server
 *                 already knows how to draw a member with their hover card and their
 *                 permissions applied, and doing it twice in two languages is how the two
 *                 drift apart.
 *
 *   who is typing whispered straight between browsers and never rendered by the server.
 *                 Typing is worthless a second later, so it is not worth a round trip and
 *                 certainly not worth a database write.
 *
 * Guests are not on the channel at all, since a presence channel cannot be authorised
 * without a session. They keep the slow poll the component already had.
 */

/* How long a typing indicator survives without another keystroke. */
const TYPING_LIFETIME = 4000;

/* Never whisper more often than this while somebody holds a key down. */
const TYPING_THROTTLE = 1500;

/* Join and leave are bursty when a page is busy, so refreshes are spaced out. */
const REFRESH_THROTTLE = 3000;

document.addEventListener('alpine:init', () => {

	Alpine.data('forumTopicPresence', (channelName, selfId = 0) => {

		/*
		 * The channel and the timer live in the closure, not on the returned object.
		 * Anything on that object becomes part of Alpine's reactive data and is wrapped in
		 * a proxy; an Echo channel is a large native object with circular references, and
		 * calling its methods back through a proxy fails with "Illegal invocation".
		 */
		let joined = false;
		let sweeper = null;

		return {

		typing: {},
		lastRefresh: 0,
		refreshQueued: false,

		init() {

			if (! window.Echo || ! channelName) {
				return;
			}

			window.Echo.join(channelName)
				.here(() => this.refresh())
				.joining(() => this.refresh())
				.leaving((user) => {
					delete this.typing[user.id];
					this.refresh();
				})
				.listenForWhisper('typing', (event) => this.onTyping(event))
				.error(() => {
					/* Not authorised, or the socket is down. The poll still covers us. */
					joined = false;
				});

			joined = true;

			/* Expire stale indicators, since a browser that closes sends no "stopped". */
			sweeper = setInterval(() => this.sweep(), 1000);

			/* Livewire keeps the DOM between navigations, so this has to unwind itself. */
			document.addEventListener('livewire:navigating', () => this.destroy(), { once: true });
		},

		destroy() {

			if (sweeper) {
				clearInterval(sweeper);
				sweeper = null;
			}

			if (joined && window.Echo) {
				window.Echo.leave(channelName);
				joined = false;
			}
		},

		onTyping(event) {

			if (! event || ! event.id || Number(event.id) === Number(selfId)) {
				return;
			}

			this.typing[event.id] = {
				name: event.name || 'Somebody',
				until: Date.now() + TYPING_LIFETIME,
			};
		},

		sweep() {

			const now = Date.now();
			let changed = false;

			for (const id of Object.keys(this.typing)) {
				if (this.typing[id].until <= now) {
					delete this.typing[id];
					changed = true;
				}
			}

			return changed;
		},

		get typingNames() {
			return Object.values(this.typing).map((entry) => entry.name);
		},

		get typingLabel() {

			const names = this.typingNames;

			if (names.length === 0) {
				return '';
			}

			if (names.length === 1) {
				return `${names[0]} is typing…`;
			}

			if (names.length === 2) {
				return `${names[0]} and ${names[1]} are typing…`;
			}

			return `${names.length} people are typing…`;
		},

		/*
		 * Ask the component to redraw, but not more than once every few seconds. A busy
		 * topic would otherwise re-render for every arrival.
		 */
		refresh() {

			const now = Date.now();
			const since = now - this.lastRefresh;

			if (since >= REFRESH_THROTTLE) {
				this.lastRefresh = now;
				this.$wire && this.$wire.$refresh();

				return;
			}

			if (this.refreshQueued) {
				return;
			}

			this.refreshQueued = true;

			setTimeout(() => {
				this.refreshQueued = false;
				this.lastRefresh = Date.now();
				this.$wire && this.$wire.$refresh();
			}, REFRESH_THROTTLE - since);
		},

		};

	});

	/*
	 * The reply box. Sends a whisper as somebody types, throttled, and nothing else.
	 * Kept separate from the panel above because the two sit in different components and
	 * only share the channel name.
	 */
	Alpine.data('forumTypingNotifier', (channelName, userId, userName) => ({

		lastSent: 0,

		notify() {

			const now = Date.now();

			if (! window.Echo || ! channelName || now - this.lastSent < TYPING_THROTTLE) {
				return;
			}

			this.lastSent = now;

			const channel = window.Echo.connector.channels[`presence-${channelName}`];

			/* Only whisper once the panel above has actually joined the channel. */
			if (channel) {
				channel.whisper('typing', { id: userId, name: userName });
			}
		},

	}));

});
